import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import Header from '../HeaderUi/Header';
import Footer from '../HomeChat/Footer.jsx';
import * as Styles from '../../Common/Styles.js';
import Fade from 'react-reveal/Fade';
import Zoom from 'react-reveal/Zoom';
import {
  FlashOn,
  CheckCircle,
  AutoAwesome,
  Search,
  FormatQuote,
  Description,
  EditNote,
  EmojiEvents,
} from '@mui/icons-material';

const ValueCard = ({ icon, title, description }) => (
  <Grid item xs={12} sm={4}>
    <Card elevation={3} sx={{ p: 3, borderRadius: '1rem', textAlign: 'center' }}>
      {icon}
      <Typography variant="h6" sx={{ mt: 2 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {description}
      </Typography>
    </Card>
  </Grid>
);

const SolutionCard = ({ icon, title, description }) => (
  <Grid item xs={12} sm={6}>
    <Card elevation={2} sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      {icon}
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2">{description}</Typography>
      </Box>
    </Card>
  </Grid>
);

const AboutParasors = () => {
  return (
    <>
      <Paper elevation={0}>
        {/* Header Section */}
        <Box
          sx={{
            position: 'relative',
            height: '30vh',
            background: '#F5F5F7',
          }}
        >
          <Box sx={{ zIndex: 1, width: '100%', position: 'absolute' }}>
            <Header />
            <Fade top duration={1000}>
              <Typography sx={{ ...Styles.headHeading, mt: 12 }} align="center">
                About Us
              </Typography>
            </Fade>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ width: '90%', mx: 'auto', mt: 4 }}>
          <Fade bottom duration={1500}>
            {/* <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              ‘Meet your in-house virtual Paralegal – Para’
            </Typography> */}
            <Typography paragraph>
              Para is India’s most advanced AI-powered legal assistant—built to transform how legal professionals research, analyze, and draft with unmatched speed and accuracy. Launched on July 25, 2024, by Unada LexiLegal AI India Private Limited, Para is purpose-built for the Indian legal ecosystem. Our mission is to empower lawyers, law firms, and businesses by eliminating inefficiencies in legal workflows and enabling sharper, more strategic legal practice through automation and intelligence.
            </Typography>
            <Typography paragraph>
              At the core of Para is a next-generation legal research engine. We offer comprehensive access not only to Supreme Court and High Court judgments dating back to 1950, but also to a wide array of tribunal decisions, statutory authorities, Central and State Acts, rules, regulations, and circulars. Our system understands context, legal principles, and precedential value—delivering results that go beyond keywords to help professionals uncover the most relevant citations with clarity and confidence.            </Typography>
            <Typography paragraph>
              Para’s document analysis capabilities are designed for deep legal comprehension at scale. Our AI can instantly scan, interpret, and summarize contracts, pleadings, legal notices, and more—extracting clauses, identifying risks, surfacing relevant case laws, and flagging inconsistencies. Whether you're reviewing a handful of documents or thousands of pages, Para ensures accuracy, reduces turnaround time, and enhances decision-making.
            </Typography>
            <Typography paragraph>
              When it comes to legal drafting, Para moves beyond conventional tools. It offers prescriptive drafting assistance by understanding the structure, tone, and statutory requirements of various document types—be it agreements, affidavits, notices, or compliance documents. Trained on millions of pages of Indian legal content, Para generates legally sound drafts that are tailored to the context, saving valuable hours and raising the standard of documentation.
            </Typography>
            <Typography paragraph>
              Para is not just a platform—it is your intelligent legal co-pilot. Our vision is to bring AI into the heart of legal practice in India, not to replace lawyers, but to empower them to work faster, think deeper, and serve better. As the legal landscape evolves, Para continues to learn and grow—delivering sharper insights, faster outputs, and a more efficient practice of law.
            </Typography>
          </Fade>

          {/* Our Values */}
          <Zoom>
            <Typography variant="h5" sx={{ fontWeight: 600, mt: 6, mb: 3 }}>
              Our Values
            </Typography>
          </Zoom>
          <Fade bottom>
            <Typography paragraph>
              Our core values reflect our commitment to:
            </Typography>
            <Grid container spacing={3}>
              <ValueCard
                icon={<FlashOn color="primary" fontSize="large" />}
                title="Innovation"
                description="We are dedicated to advancing legal technology, constantly refining our solutions to address the evolving challenges faced by legal professionals."
              />
              <ValueCard
                icon={<CheckCircle color="success" fontSize="large" />}
                title="Accuracy"
                description="We prioritize precision in our tools and services, ensuring that our users can trust the quality of the information and insights they receive."
              />
              <ValueCard
                icon={<AutoAwesome color="secondary" fontSize="large" />}
                title="Empowerment"
                description="We aim to empower legal practitioners by simplifying complex tasks, allowing them to focus on delivering superior legal outcomes."
              />
            </Grid>
          </Fade>

          {/* Our Solutions */}
          <Fade bottom duration={2000}>
            <Typography variant="h5" sx={{ fontWeight: 600, mt: 6, mb: 2 }}>
              Our Solutions
            </Typography>
            <Typography paragraph>
              Parasors.in offers a suite of AI-driven tools, each tailored to
              meet the varied needs of legal professionals:
            </Typography>

            <Grid container spacing={3}>
              <SolutionCard
                icon={<Search color="primary" fontSize="large" />}
                title="ParaSearch"
                description="Enables in-depth case law research, leveraging advanced AI technology to quickly identify relevant judgments and legal precedents."
              />
              <SolutionCard
                icon={<FormatQuote color="info" fontSize="large" />}
                title="ParaSors"
                description="Facilitates seamless citation management, helping legal professionals maintain accuracy and organization in their references."
              />
              <SolutionCard
                icon={<Description color="warning" fontSize="large" />}
                title="ParaDoc"
                description="Will bring a new level of sophistication to document analysis, offering insights that enhance legal understanding."
              />
              <SolutionCard
                icon={<EditNote color="success" fontSize="large" />}
                title="ParaDraft (Coming Soon)"
                description="Will streamline the drafting process, incorporating AI to enhance the quality and accuracy of legal documents."
              />
            </Grid>
          </Fade>

          {/* Leadership */}
          <Zoom>
            <Typography variant="h5" sx={{ fontWeight: 600, mt: 6, mb: 3 }}>
              Leadership
            </Typography>
          </Zoom>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6">
                  Rohan Hundia – CEO
                </Typography>
                <Typography variant="body2">
                  Leading Parasors.in with a strategic vision for revolutionizing legal technology.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6">
                  Aditya Vijay – CTO
                </Typography>
                <Typography variant="body2">
                  Driving technological advancements and ensuring AI capabilities are state-of-the-art.
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Final Words */}
          <Fade>
            <Divider sx={{ my: 6 }} />
            <Typography paragraph>
              Parasors.in distinguishes itself as more than just a legal research tool; it is a comprehensive platform that brings together technology, legal expertise, and an intuitive user experience, all tailored for the Indian legal system.
            </Typography>
            <Typography paragraph>
              Our dedication to innovation and excellence ensures that legal professionals can navigate the complexities of law with confidence and agility.
            </Typography>
            <Typography paragraph>
              Experience the future of legal research and document management with Parasors.in—where precision meets progress.
            </Typography>
          </Fade>
        </Box>

        {/* Footer */}
        <Footer />
      </Paper>
    </>
  );
};

export default AboutParasors;
